// These two types are declared alongside the components that render the price
// breakdowns and those components consume this store. `import type` keeps the
// dependency compile-time only so no runtime require cycle is created.
import type { NewParkingSessionPriceBreakDownDetailsProps } from 'components/ongoingParking/NewParkingSessionPriceBreakdownDetails';
import type { OngoingParkingSessionPriceBreakdownType } from 'components/ongoingParking/OngoingParkingSessionPriceBreakdownDetails';
import {
  ConfirmCreateSalesOrderDataProps,
  ParkingZoneTab,
  ParkzonePmcUnion,
} from 'types/common';
import {
  CountryCode,
  ParkZone,
  ReservedParkingSpace,
  SegmentProps,
} from 'types/generatedSchemaTypes';
import {
  ParkingPricingProps,
  ParkingSpaceData,
  UnlockTariffItem,
} from 'types/states';
import { create } from 'zustand';

interface ParkzoneState {
  parkZonesData: ParkZone[];
  filterParkzoneData: ParkZone[];
  setParkzoneData: (data: ParkZone[]) => void;
  setFilterParkzoneData: (data: ParkZone[]) => void;
}

// Parkzone / pricing / tariff state migrated from reactive vars
//
// selectedParkingZoneTab, parkZoneCountry, parkingVehicleLicensePlate and
// parkingPaymentCardId are typed wider than the vars they replace: makeVar
// writes on
// `arguments.length > 0`, so their `someVar(undefined)` resets really stored
// undefined against a non-nullable declaration. That undefined carries meaning
// — Tariff gates its private-tariff checkmark on `selectedTab === 'PARK'`, and
// '' means "never chosen" where undefined means "cleared". parkZoneCountry is
// declared `| null` but written from a Maybe<CountryCode>, and the difference
// shows: SelectPaymentMethodModalContent compares it with
// `benefit.countryCode === zoneCountryCode`, which differs for null vs
// undefined. selectedTariffId's undefined likewise cues useTariffs to
// auto-select a default.
type ParkzoneSelectionState = {
  selectedParkingZoneTab: ParkingZoneTab | undefined;
  parkZoneCountry: CountryCode | null | undefined;
  selectedParkzonePmcUnion: ParkzonePmcUnion;
  parkingPricingInfo: ParkingPricingProps;
  selectedTariffId: string | undefined;
  selectedTariffName: string | undefined;
  selectedChargingPricingInfoSegmentTariffPlanId: string | undefined;
  selectedChargingPricingInfoSegment: SegmentProps | undefined;
  unlockTariffItem: UnlockTariffItem | undefined;
  newParkingSessionPriceBreakdownValues:
    | NewParkingSessionPriceBreakDownDetailsProps
    | undefined;
  ongoingParkingSessionPriceBreakdownValues:
    | OngoingParkingSessionPriceBreakdownType
    | undefined;
  selectedParkingSpaces: ReservedParkingSpace[] | undefined;
  selectedParkingSpace: ParkingSpaceData | undefined;
  confirmCreateSalesOrderData: ConfirmCreateSalesOrderDataProps;
  parkingVehicleLicensePlate: string | undefined;
  parkingPaymentCardId: string | undefined;
};

type ParkzoneSelectionActions = {
  setSelectedParkingZoneTab: (tab: ParkingZoneTab | undefined) => void;
  setParkZoneCountry: (country: CountryCode | null | undefined) => void;
  setSelectedParkzonePmcUnion: (pmcUnion: ParkzonePmcUnion) => void;
  setParkingPricingInfo: (info: ParkingPricingProps) => void;
  setSelectedTariffId: (tariffId: string | undefined) => void;
  setSelectedTariffName: (tariffName: string | undefined) => void;
  setSelectedChargingPricingInfoSegmentTariffPlanId: (
    tariffPlanId: string | undefined
  ) => void;
  setSelectedChargingPricingInfoSegment: (
    segment: SegmentProps | undefined
  ) => void;
  setUnlockTariffItem: (item: UnlockTariffItem | undefined) => void;
  setNewParkingSessionPriceBreakdownValues: (
    values: NewParkingSessionPriceBreakDownDetailsProps | undefined
  ) => void;
  setOngoingParkingSessionPriceBreakdownValues: (
    values: OngoingParkingSessionPriceBreakdownType | undefined
  ) => void;
  setSelectedParkingSpaces: (
    spaces: ReservedParkingSpace[] | undefined
  ) => void;
  setSelectedParkingSpace: (space: ParkingSpaceData | undefined) => void;
  setConfirmCreateSalesOrderData: (
    data: ConfirmCreateSalesOrderDataProps
  ) => void;
  setParkingVehicleLicensePlate: (plateText: string | undefined) => void;
  setParkingPaymentCardId: (cardId: string | undefined) => void;
};

// The values the reactive vars were seeded with in states/common.ts — not the
// values their reset paths write (see the type note above).
const initialParkzoneSelectionState: ParkzoneSelectionState = {
  selectedParkingZoneTab: 'PARK',
  parkZoneCountry: null,
  selectedParkzonePmcUnion: undefined,
  parkingPricingInfo: {
    parkingType: 'normal',
    poolingId: undefined,
    poolingFull: false,
  },
  selectedTariffId: undefined,
  selectedTariffName: undefined,
  selectedChargingPricingInfoSegmentTariffPlanId: undefined,
  selectedChargingPricingInfoSegment: undefined,
  unlockTariffItem: undefined,
  newParkingSessionPriceBreakdownValues: undefined,
  ongoingParkingSessionPriceBreakdownValues: undefined,
  selectedParkingSpaces: undefined,
  selectedParkingSpace: undefined,
  confirmCreateSalesOrderData: undefined,
  parkingVehicleLicensePlate: '',
  parkingPaymentCardId: '',
};

export const useParkzoneStore = create<
  ParkzoneState & ParkzoneSelectionState & ParkzoneSelectionActions
>((set) => ({
  parkZonesData: [],
  filterParkzoneData: [],
  setParkzoneData: (data) => set({ parkZonesData: data }),
  setFilterParkzoneData: (data) => set({ filterParkzoneData: data }),

  ...initialParkzoneSelectionState,

  setSelectedParkingZoneTab: (tab) => set({ selectedParkingZoneTab: tab }),
  setParkZoneCountry: (country) => set({ parkZoneCountry: country }),
  setSelectedParkzonePmcUnion: (pmcUnion) =>
    set({ selectedParkzonePmcUnion: pmcUnion }),
  setParkingPricingInfo: (info) => set({ parkingPricingInfo: info }),
  setSelectedTariffId: (tariffId) => set({ selectedTariffId: tariffId }),
  setSelectedTariffName: (tariffName) =>
    set({ selectedTariffName: tariffName }),
  setSelectedChargingPricingInfoSegmentTariffPlanId: (tariffPlanId) =>
    set({ selectedChargingPricingInfoSegmentTariffPlanId: tariffPlanId }),
  setSelectedChargingPricingInfoSegment: (segment) =>
    set({ selectedChargingPricingInfoSegment: segment }),
  setUnlockTariffItem: (item) => set({ unlockTariffItem: item }),
  setNewParkingSessionPriceBreakdownValues: (values) =>
    set({ newParkingSessionPriceBreakdownValues: values }),
  setOngoingParkingSessionPriceBreakdownValues: (values) =>
    set({ ongoingParkingSessionPriceBreakdownValues: values }),
  setSelectedParkingSpaces: (spaces) => set({ selectedParkingSpaces: spaces }),
  setSelectedParkingSpace: (space) => set({ selectedParkingSpace: space }),
  setConfirmCreateSalesOrderData: (data) =>
    set({ confirmCreateSalesOrderData: data }),
  setParkingVehicleLicensePlate: (plateText) =>
    set({ parkingVehicleLicensePlate: plateText }),
  setParkingPaymentCardId: (cardId) => set({ parkingPaymentCardId: cardId }),
}));

export const getParkzoneState = () => useParkzoneStore.getState();
